import React, { useEffect, useMemo, useState } from 'react';
import {
  DownloadIcon,
  TableIcon,
  TableRowsIcon,
  ChevronRightIcon,
  GridIcon,
  SlidersIcon,
} from '@revfluence/fresh-icons/solid/esm';
import { useParams } from 'react-router-dom';
import { useMemberListContext } from '@frontend/app/context/MemberListContext';
import { useMemberSearchQueryVariables } from '@frontend/app/components/MemberTable/hooks';
import { useMemberSearchCountQuery, useMemberSearchQuery } from '@frontend/app/hooks';
import { useLazyQuery } from '@apollo/client';
import { generateMemberSearchGraphQLQuery } from '@frontend/app/queries';
import { format } from 'date-fns';
import { SearchInput } from '@frontend/app/refresh-components';
import { Popover, PopoverContent, PopoverTrigger } from '@frontend/shadcn/components/ui/popover';
import { Pagination } from '@frontend/app/components/MembersTableOld/Pagination';
import { useOrderedMemberColumns } from '@frontend/app/hooks/memberList';
import { toast } from '@frontend/shadcn/hooks/use-toast';
import { useClientFeatureEnabled } from '@frontend/app/hooks';
import { ClientFeature } from '@frontend/app/constants';
import { useMembersTableFiltersContext } from '@frontend/app/components/MembersTableOld/MembersTableFiltersDrawer/MembersTableFiltersDrawerContext';
import { Button } from '@/shadcn/components/ui/button';
import { ProspectsEditColumns } from './ProspectsEditColumns';

export const ProspectsListToolbar = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const programId = projectId ? parseInt(projectId, 10) : undefined;

  const {
    columnVisibility,
    searchQuery,
    searchText,
    updateSearchText,
    page,
    updatePage,
    columns,
    segment,
    updateView,
    view,
    pageSize,
    updatePageSize,
    updateColumnVisibility
  } = useMemberListContext();

  const { setIsDrawerOpen } = useMembersTableFiltersContext();

  const isWorkflowEnabled = useClientFeatureEnabled(ClientFeature.WORKFLOW);

  const [localSearchText, setLocalSearchText] = useState(searchText);
  const [openFilterPage, setOpenFilterPage] = useState(false);
  const [openRowsPerPage, setOpenRowsPerPage] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const memberSearchQueryVariables = useMemberSearchQueryVariables(searchQuery, columnVisibility, pageSize);

  const skipLoadingMembers = useMemo(() => !searchQuery || !columnVisibility, [searchQuery, columnVisibility]);

  const { data: { members = null } = {} } = useMemberSearchQuery({
    variables: {
      ...memberSearchQueryVariables,
      skip: (page - 1) * pageSize,
      take: pageSize,
    },
    skip: skipLoadingMembers,
    fetchPolicy: 'cache-first',
  });

  const { data: totalCountData } = useMemberSearchCountQuery({
    variables: {
      query: memberSearchQueryVariables?.query,
    },
    skip: !memberSearchQueryVariables?.query,
  });

  const visibleColumns = useOrderedMemberColumns(columns, segment);

  // Lazy query for downloading all members
  const [fetchAllMembers] = useLazyQuery(generateMemberSearchGraphQLQuery(), {
    fetchPolicy: 'network-only',
  });

  // Handle search text debouncing
  useEffect(() => {
    const debouncedSearchText = setTimeout(() => {
      if (localSearchText !== searchText) {
        updateSearchText(localSearchText);
        updatePage(1);
      }
    }, 500);
    return () => clearTimeout(debouncedSearchText);
  }, [localSearchText, updateSearchText, searchText, updatePage]);

  // Reset page when page size changes
  useEffect(() => {
    updatePage(1);
  }, [pageSize, updatePage]);

  const handleDownloadCsv = async () => {
    if (!visibleColumns || visibleColumns.length === 0) {
      toast({
        title: 'Export Error',
        description: 'No columns available to export',
        variant: 'destructive',
      });
      return;
    }

    if (!totalCountData?.count || totalCountData.count === 0) {
      toast({
        title: 'Export Error',
        description: 'No data available to export',
        variant: 'destructive',
      });
      return;
    }

    if (!memberSearchQueryVariables?.query) {
      toast({
        title: 'Export Error',
        description: 'No search query available for export',
        variant: 'destructive',
      });
      return;
    }

    setIsDownloading(true);

    try {
      // Fetch all members for CSV export
      const { data: allMembersData } = await fetchAllMembers({
        variables: {
          ...memberSearchQueryVariables,
          take: totalCountData.count,
          skip: 0,
          includeTags: true,
          sortDirection: memberSearchQueryVariables.query?.orderBy?.direction || null,
        },
      });

      if (!allMembersData?.members || allMembersData.members.length === 0) {
        toast({
          title: 'Export Error',
          description: 'No members data received for export',
          variant: 'destructive',
        });
        return;
      }

      const allMembers = allMembersData.members;

      // Helper function to escape CSV values
      const escapeCSVValue = (value): string => {
        if (value === null || value === undefined) {
          return '';
        }

        // Handle arrays
        if (Array.isArray(value)) {
          if (value.length === 0) {
            return '';
          }

          const arrayStrings = value.map((item) => {
            if (typeof item === 'object' && item !== null) {
              if (item.name) return item.name;
              if (item.title) return item.title;
              if (item.label) return item.label;
              if (item.id && item.email) return `${item.email} (ID: ${item.id})`;
              if (item.id) return `ID: ${item.id}`;
              return JSON.stringify(item);
            }
            return String(item);
          });

          return arrayStrings.join('; ');
        }

        // Handle objects
        if (typeof value === 'object' && value !== null) {
          if (value.name) return String(value.name);
          if (value.title) return String(value.title);
          if (value.label) return String(value.label);
          if (value.email) return String(value.email);
          if (value.id && value.email) return `${value.email} (ID: ${value.id})`;
          if (value.id) return `ID: ${value.id}`;

          try {
            return JSON.stringify(value);
          } catch (e) {
            return '[Complex Object]';
          }
        }

        // Handle primitive values
        const stringValue = String(value);

        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n') || stringValue.includes('\r')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }

        return stringValue;
      };

      // Create header row
      const headers = visibleColumns.map((column) => escapeCSVValue(column.headerName || column.field)).join(',');

      // Create data rows
      const dataRows = allMembers.map((member) => {
        return visibleColumns.map((column) => {
          let value;
          if (column.field.includes('.')) {
            const fieldParts = column.field.split('.');
            value = fieldParts.reduce((obj, part) => obj?.[part], member);
          } else {
            value = member[column.field];
          }

          return escapeCSVValue(value);
        }).join(',');
      });

      // Combine headers and data
      const csvContent = [headers, ...dataRows].join('\n');

      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);

      const timestamp = format(new Date(), 'yyyy-MM-dd_HH-mm-ss');
      const filename = `prospects_${timestamp}.csv`;

      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error('Error generating CSV:', error);
      toast({
        title: 'Export Error',
        description: 'Failed to generate CSV file. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsDownloading(false);
    }
  };

  // Handle page size change with proper state management
  const handlePageSizeChange = (newPageSize: number) => {
    updatePageSize(newPageSize);
    setOpenRowsPerPage(false);
  };

  return (
    <div className="flex items-center gap-2">
      <SearchInput
        searchText={localSearchText}
        setSearchText={setLocalSearchText}
      />
      <Button
        size="headerIcon"
        variant="ghost"
        disabled={isDownloading || !members?.length}
        onClick={handleDownloadCsv}
        title={isDownloading ? 'Exporting...' : 'Export to CSV'}
        loading={isDownloading}
      >
        <DownloadIcon />
      </Button>
      <Button
        size="headerIcon"
        variant="ghost"
        onClick={() => setIsDrawerOpen(true)}
        title="Advanced Filters"
      >
        <SlidersIcon />
      </Button>
      <Popover open={openFilterPage} onOpenChange={setOpenFilterPage}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="sm" title="View Options">
            <TableIcon />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          onMouseEnter={() => setOpenFilterPage(true)}
          onMouseLeave={() => setOpenFilterPage(false)}
          className="w-30 p-2"
        >
          <div className="flex flex-col space-y-2">
            <ProspectsEditColumns
              communityId={undefined}
              programId={programId}
              segment={segment}
              visibility={columnVisibility}
              onChange={updateColumnVisibility}
              isWorkflowEnabled={isWorkflowEnabled}
            />
            <div
              onClick={() => {
                updateView(view === 'list' ? 'grid' : 'list');
              }}
              className="flex items-center gap-2 hover:bg-gray-100 p-1 rounded hover:cursor-pointer"
            >
              {view === 'list' ? <GridIcon /> : <TableIcon />}
              <span className="flex-1">{view === 'list' ? 'Card View' : 'Table View'}</span>
            </div>
            <Popover open={openRowsPerPage} onOpenChange={setOpenRowsPerPage}>
              <PopoverTrigger asChild>
                <div className="flex items-center gap-2 hover:bg-gray-100 p-1 rounded hover:cursor-pointer">
                  <TableRowsIcon />
                  <span className="flex-1">Rows per Page</span>
                  <ChevronRightIcon className="w-4 h-4" />
                </div>
              </PopoverTrigger>
              <PopoverContent className="w-44 p-2 absolute left-full top-0">
                <div className="flex flex-col space-y-1">
                  {[50, 100, 200].map((size) => (
                    <button
                      key={size}
                      className={`px-3 py-2 text-sm rounded w-full text-left hover:cursor-pointer
        ${size === pageSize ? 'bg-gray-200 font-semibold cursor-not-allowed' : 'hover:bg-gray-100'}`}
                      onClick={() => handlePageSizeChange(size)}
                      disabled={size === pageSize}
                    >
                      {`${size} Rows`}
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </PopoverContent>
      </Popover>
      <Pagination
        total={totalCountData?.count || 0}
        page={page}
        setPage={updatePage}
        pageSize={pageSize}
        showTotal
      />
    </div>
  );
};
